import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { TrendingUp, DollarSign, Home, Sun, Palmtree, Waves } from 'lucide-react';
import { Button } from './ui/button';

const WHATSAPP_LINK = "https://wa.me/529841585732?text=Hola%2C%20quiero%20una%20asesor%C3%ADa%20de%20inversi%C3%B3n%20inmobiliaria%20en%20Riviera%20Maya";

export const InvestmentZones = () => {
  const { language } = useLanguage();

  const zones = [
    {
      id: 'tulum',
      icon: Sun,
      name: 'Tulum',
      tagline: language === 'es' 
        ? 'Rentabilidad y Plusvalía en el Paraíso del Caribe'
        : 'Profitability and Appreciation in Caribbean Paradise',
      description: language === 'es'
        ? 'Invertir en Tulum es asegurar tu lugar en uno de los destinos más exclusivos y rentables del mundo. Con proyectos como el Aeropuerto Internacional de Tulum y el Tren Maya, la conectividad y crecimiento están garantizados. Desde playas vírgenes hasta proyectos boutique, Tulum ofrece una oportunidad única para quienes buscan una inversión que se revalorice mientras disfrutan de un paraíso incomparable.'
        : 'Investing in Tulum means securing your place in one of the most exclusive and profitable destinations in the world. With projects like the Tulum International Airport and the Mayan Train, connectivity and growth are guaranteed.',
      stats: {
        roi: '8-15%',
        appreciation: '12-15%',
        nightlyRate: '$200-$800 USD',
        occupancy: '78-85%'
      },
      color: 'from-[#2BBFBF] to-[#0A2235]'
    },
    {
      id: 'playaDelCarmen',
      icon: Palmtree,
      name: 'Playa del Carmen',
      tagline: language === 'es'
        ? 'El destino donde la rentabilidad nunca duerme'
        : 'The destination where profitability never sleeps',
      description: language === 'es'
        ? 'Invertir en Playa del Carmen es apostar por un destino consolidado que combina estilo de vida vibrante y alta rentabilidad. La creciente plusvalía, impulsada por desarrollos inmobiliarios y una infraestructura sólida, asegura que tu inversión se revaloriza año tras año. Su icónica Quinta Avenida y la constante afluencia de turistas internacionales hacen de PDC un punto clave para inversiones seguras.'
        : 'Investing in Playa del Carmen means betting on a consolidated destination that combines vibrant lifestyle and high profitability. Growing appreciation driven by real estate developments ensures your investment gains value year after year.',
      stats: {
        roi: '7-12%',
        appreciation: '10-12%',
        nightlyRate: '$150-$600 USD',
        occupancy: '74-80%'
      },
      color: 'from-[#7ECFCF] to-[#0A2235]'
    },
    {
      id: 'puertoMorelos',
      icon: Waves,
      name: 'Puerto Morelos',
      tagline: language === 'es'
        ? 'Inversión tranquila con crecimiento asegurado'
        : 'Peaceful investment with guaranteed growth',
      description: language === 'es'
        ? 'En Puerto Morelos, la belleza natural se combina con oportunidades de inversión únicas. Este tranquilo pueblo costero, conocido por su arrecife de coral y enfoque ecológico, es ideal para quienes buscan equilibrio entre exclusividad y rentabilidad. Su cercanía a Cancún y Playa del Carmen asegura una constante plusvalía. Invertir aquí es apostar por un destino en crecimiento sostenible.'
        : 'In Puerto Morelos, natural beauty combines with unique investment opportunities. This peaceful coastal town, known for its coral reef and ecological focus, is ideal for those seeking balance between exclusivity and profitability.',
      stats: {
        roi: '8-11%',
        appreciation: '15%+',
        nightlyRate: '$100-$300 USD',
        occupancy: '70-75%'
      },
      color: 'from-[#D4EDE8] to-[#0A2235]'
    }
  ];

  return (
    <section className="bg-[#0A2235] py-16 md:py-[60px]" data-testid="investment-zones-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl text-[#FAFAF7] mb-4 tracking-tight">
            {language === 'es' 
              ? 'Riviera Maya: Inversión con Rendimiento en el Paraíso'
              : 'Riviera Maya: Investment with Returns in Paradise'}
          </h2>
          <p className="font-inter text-[#FAFAF7]/70 text-base sm:text-lg font-light max-w-3xl mx-auto">
            {language === 'es'
              ? 'La Riviera Maya es el destino ideal para quienes buscan alta rentabilidad y exclusividad. Con proyectos estratégicos como el Tren Maya y un mercado inmobiliario en expansión, invertir aquí asegura rentabilidad, plusvalía y un lugar en el paraíso.'
              : 'The Riviera Maya is the ideal destination for those seeking high profitability and exclusivity. With strategic projects like the Mayan Train and an expanding real estate market, investing here ensures profitability and appreciation.'}
          </p>
        </div>

        {/* Zone Cards */}
        <div className="space-y-12">
          {zones.map((zone, index) => (
            <div
              key={zone.id}
              className={`relative overflow-hidden ${index % 2 === 0 ? '' : 'md:flex-row-reverse'}`}
              data-testid={`zone-info-${zone.id}`}
            >
              <div className="bg-[#FAFAF7]/5 backdrop-blur-md border border-[#FAFAF7]/10 p-8 md:p-12 rounded-[20px]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                  {/* Content */}
                  <div className={index % 2 === 0 ? '' : 'lg:order-2'}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 bg-gradient-to-br ${zone.color} rounded-lg flex items-center justify-center`}>
                        <zone.icon className="w-6 h-6 text-[#FAFAF7]" />
                      </div>
                      <div>
                        <h3 className="font-playfair text-2xl md:text-3xl text-[#FAFAF7]">
                          {zone.name}
                        </h3>
                      </div>
                    </div>
                    
                    <p className="font-playfair text-lg text-[#2BBFBF] mb-4 italic">
                      {zone.tagline}
                    </p>
                    
                    <p className="font-inter text-[#FAFAF7]/70 leading-[1.7] mb-6 font-light">
                      {zone.description}
                    </p>

                    <Button
                      asChild
                      className="bg-[#2BBFBF] hover:bg-[#7ECFCF] text-[#0A2235] font-medium rounded-full"
                    >
                      <a href={`${WHATSAPP_LINK}%20en%20${zone.name}`} target="_blank" rel="noopener noreferrer">
                        {language === 'es' ? `Invertir en ${zone.name}` : `Invest in ${zone.name}`}
                      </a>
                    </Button>
                  </div>

                  {/* Stats */}
                  <div className={`grid grid-cols-2 gap-4 ${index % 2 === 0 ? '' : 'lg:order-1'}`}>
                    <div className="bg-[#0A2235] p-6 text-center">
                      <TrendingUp className="w-6 h-6 text-[#2BBFBF] mx-auto mb-2" />
                      <p className="font-playfair text-2xl md:text-3xl text-[#2BBFBF] mb-1">
                        {zone.stats.roi}
                      </p>
                      <p className="font-inter text-xs text-[#FAFAF7]/60 uppercase tracking-wider">
                        {language === 'es' ? 'Rendimiento Anual' : 'Annual Yield'}
                      </p>
                    </div>
                    
                    <div className="bg-[#0A2235] p-6 text-center">
                      <TrendingUp className="w-6 h-6 text-[#7ECFCF] mx-auto mb-2" />
                      <p className="font-playfair text-2xl md:text-3xl text-[#7ECFCF] mb-1">
                        {zone.stats.appreciation}
                      </p>
                      <p className="font-inter text-xs text-[#FAFAF7]/60 uppercase tracking-wider">
                        {language === 'es' ? 'Plusvalía' : 'Appreciation'}
                      </p>
                    </div>
                    
                    <div className="bg-[#0A2235] p-6 text-center">
                      <DollarSign className="w-6 h-6 text-[#D4EDE8] mx-auto mb-2" />
                      <p className="font-playfair text-lg md:text-xl text-[#FAFAF7] mb-1">
                        {zone.stats.nightlyRate}
                      </p>
                      <p className="font-inter text-xs text-[#FAFAF7]/60 uppercase tracking-wider">
                        {language === 'es' ? 'Renta/Noche' : 'Nightly Rate'}
                      </p>
                    </div>
                    
                    <div className="bg-[#0A2235] p-6 text-center">
                      <Home className="w-6 h-6 text-[#FAFAF7] mx-auto mb-2" />
                      <p className="font-playfair text-2xl md:text-3xl text-[#FAFAF7] mb-1">
                        {zone.stats.occupancy}
                      </p>
                      <p className="font-inter text-xs text-[#FAFAF7]/60 uppercase tracking-wider">
                        {language === 'es' ? 'Ocupación' : 'Occupancy'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* News Section */}
        <div className="mt-20">
          <h3 className="font-playfair text-2xl text-[#FAFAF7] text-center mb-8">
            {language === 'es' ? 'Noticias del Mercado' : 'Market News'}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-[#FAFAF7]/5 p-6 border border-[#FAFAF7]/10 rounded-[20px]">
              <p className="font-inter text-sm text-[#2BBFBF] uppercase tracking-wider mb-2">
                {language === 'es' ? 'Infraestructura' : 'Infrastructure'}
              </p>
              <h4 className="font-playfair text-lg text-[#FAFAF7] mb-2">
                {language === 'es' 
                  ? 'Tren Maya y Aeropuerto de Tulum impulsan la región'
                  : 'Mayan Train and Tulum Airport boost the region'}
              </h4>
              <p className="font-inter text-sm text-[#FAFAF7]/60 leading-[1.7]">
                {language === 'es'
                  ? 'Los proyectos estratégicos consolidan a la Riviera Maya como imán para inversores nacionales e internacionales.'
                  : 'Strategic projects consolidate the Riviera Maya as a magnet for national and international investors.'}
              </p>
            </div>
            
            <div className="bg-[#FAFAF7]/5 p-6 border border-[#FAFAF7]/10 rounded-[20px]">
              <p className="font-inter text-sm text-[#2BBFBF] uppercase tracking-wider mb-2">
                {language === 'es' ? 'Mercado' : 'Market'}
              </p>
              <h4 className="font-playfair text-lg text-[#FAFAF7] mb-2">
                {language === 'es'
                  ? 'Tulum y PDC: Líderes en crecimiento y plusvalía'
                  : 'Tulum and PDC: Leaders in growth and appreciation'}
              </h4>
              <p className="font-inter text-sm text-[#FAFAF7]/60 leading-[1.7]">
                {language === 'es'
                  ? 'Ambos destinos lideran el mercado con atractivas opciones para vivir o generar ingresos.'
                  : 'Both destinations lead the market with attractive options for living or generating income.'}
              </p>
            </div>
            
            <div className="bg-[#FAFAF7]/5 p-6 border border-[#FAFAF7]/10 rounded-[20px]">
              <p className="font-inter text-sm text-[#2BBFBF] uppercase tracking-wider mb-2">
                {language === 'es' ? 'Ranking' : 'Ranking'}
              </p>
              <h4 className="font-playfair text-lg text-[#FAFAF7] mb-2">
                {language === 'es'
                  ? 'Riviera Maya: #1 para invertir en 2026'
                  : 'Riviera Maya: #1 to invest in 2026'}
              </h4>
              <p className="font-inter text-sm text-[#FAFAF7]/60 leading-[1.7]">
                {language === 'es'
                  ? 'La región emerge como epicentro de la inversión inmobiliaria en México.'
                  : 'The region emerges as the epicenter of real estate investment in Mexico.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
